// Single source of truth for the homepage's editable text.
//
// HOMEPAGE_DEFAULTS holds the original hardcoded copy verbatim. The admin can
// override any of these strings (stored in admin_config.homepage, served via
// /public-config). useHomepageContent() deep-merges the saved overrides OVER
// these defaults, so any missing/empty field falls back to the original copy —
// an empty or failed config renders exactly like the original site.
//
// Structure is FIXED: the number of steps/testimonials/trust cards/etc. matches
// the defaults. Overrides are merged by index; extra items are ignored and
// missing ones fall back. Icons, the decorative mock-up report data, avatar
// images and numeric formatting (prefix/suffix/decimals) stay in the components.
import { useMemo } from "react";
import { usePublicConfig } from "@/lib/usePublicConfig";

export const HOMEPAGE_DEFAULTS = {
  hero: {
    eyebrow: "Slimme offerte-analyse",
    // Rendered as: {headlineLead} <accent>{headlineAccent}</accent>
    headlineLead: "Is jouw offerte",
    headlineAccent: "écht correct geprijsd?",
    // Rendered as: {subtitleLead}<bold>{subtitleAccent}</bold>
    subtitleLead:
      "Wij vergelijken jouw prijs met wat anderen in jouw regio betalen. In 60 seconden weet je of je goed zit — of ",
    subtitleAccent: "hoeveel je bespaart.",
    ctaLabel: "Analyseer mijn offerte — €4,99",
    ctaSub: "Geen abonnement · Resultaat in 60 sec",
    badgeRatingValue: "4,9/5",
    badgeRatingLabel: "op 1.247 analyses",
    badgeGdpr: "GDPR · 30 dagen",
    badgeCountry: "België & Nederland",
    badgeAudience: "Voor particulieren",
  },
  stats: [
    { value: "4847", prefix: "", suffix: "+", decimals: 0, label: "Offertes geanalyseerd" },
    { value: "1.2", prefix: "€", suffix: "M+", decimals: 1, label: "Bespaard voor gebruikers" },
    { value: "38", prefix: "", suffix: "%", decimals: 0, label: "Gemiddelde overprijzing ontdekt" },
    { value: "60", prefix: "", suffix: " sec", decimals: 0, label: "Gemiddelde analysetijd" },
  ],
  howItWorks: {
    eyebrow: "Hoe werkt het",
    heading: "Van offerte naar zekerheid in 3 stappen",
    intro:
      "Eerlijk, snel en zonder vakjargon. Je krijgt een rapport dat je daadwerkelijk kan gebruiken aan tafel met je aannemer.",
    steps: [
      {
        title: "Upload je offerte",
        desc: "Upload je offerte als PDF, foto of typ de bedragen over. We ondersteunen alle types: renovatie, elektriciteit, schilderwerk, IT en meer.",
      },
      {
        title: "Wij analyseren elke lijnpost",
        desc: "Ons platform vergelijkt elke post met actuele marktprijzen in jouw regio. Materiaal, arbeidsuren, overhead — niets wordt overgeslagen.",
      },
      {
        title: "Ontvang je persoonlijk rapport",
        desc: "Je krijgt een gedetailleerde heatmap met voor elke post de marktprijs, het verschil én concrete onderhandelingstips.",
      },
    ],
  },
  reportPreview: {
    eyebrow: "Het rapport",
    heading: "Zo ziet jouw rapport eruit",
    intro:
      "Een gedetailleerde heatmap. Per post de marktprijs, het verschil én een concrete onderhandelingstip. Geen vakjargon — wel duidelijke acties.",
    bullets: [
      "Score van 0 tot 100 met directe duiding",
      "Marktprijs per lijnpost (min – max)",
      "Drie concrete onderhandelingstips",
      "PDF-rapport om te delen of te bewaren",
    ],
    ctaLabel: "Maak mijn eigen rapport",
    disclaimer: "Dit is een voorbeeld. Jouw rapport is gebaseerd op jouw offerte.",
  },
  audiences: {
    eyebrow: "Voor wie",
    heading: "Voor wie is OfferteExpert?",
    items: [
      { title: "Huiseigenaars", desc: "Renovatie, verbouwing of herstellingen? Weet voor je tekent of de prijs klopt." },
      { title: "Huurders", desc: "Ontvang je facturen voor herstellingen? Check of je verhuurder je niet overvraagt." },
      { title: "KMO's en ondernemers", desc: "Vergelijk offertes voor IT, onderhoud of verbouwingen snel en objectief." },
      { title: "Vastgoedprofessionals", desc: "Bied je klanten extra service door offertes professioneel te laten valideren." },
    ],
  },
  trust: {
    eyebrow: "Vertrouwen",
    heading: "Waarom mensen OfferteExpert vertrouwen",
    items: [
      { title: "Geen abonnement", desc: "Je betaalt enkel per analyse. Geen verborgen kosten." },
      { title: "GDPR-conform", desc: "Documenten worden 30 dagen bewaard en daarna automatisch verwijderd." },
      { title: "Slimme marktanalyse", desc: "Getraind op duizenden Belgische en Nederlandse marktprijzen." },
      { title: "Onafhankelijk", desc: "Geen banden met aannemers of leveranciers. Objectief advies." },
      { title: "Beveiligde uploads", desc: "Alle bestanden worden versleuteld opgeslagen." },
      { title: "Belgisch bedrijf", desc: "Gemaakt en beheerd door Webtron BV in Waregem, België." },
    ],
  },
  testimonials: {
    eyebrow: "Stemmen uit de praktijk",
    heading: "Wat gebruikers zeggen",
    items: [
      {
        name: "Marie V.",
        location: "Gent",
        quote:
          "Ik had een offerte voor dakisolatie van €8.400. OfferteExpert toonde aan dat ik €2.100 te veel betaalde. Na onderhandeling kreeg ik de werken voor €6.200 gedaan. De €4,99 verdiende ik duizendvoudig terug.",
      },
      {
        name: "Pieter D.",
        location: "Antwerpen",
        quote:
          "Als aannemer gebruik ik OfferteExpert zelf om mijn eigen offertes te benchmarken. Zo weet ik dat mijn prijzen eerlijk zijn en kan ik klanten dit ook bewijzen.",
      },
      {
        name: "Nathalie K.",
        location: "Brugge",
        quote:
          "Eenvoudig, snel en verrassend nauwkeurig. De onderhandelingstips alleen al waren de prijs meer dan waard. Aanrader voor iedereen die verbouwt.",
      },
    ],
  },
  pricing: {
    eyebrow: "Eerlijk geprijsd",
    heading: "Eén heldere prijs. Of bespaar met een 3-pack.",
    single: {
      label: "Per analyse",
      price: "€4,99",
      sub: "Eenmalig — BTW inbegrepen",
      features: [
        "Volledige heatmap-analyse",
        "Concrete onderhandelingstips",
        "PDF-rapport (downloadbaar)",
        "Geen abonnement, eenmalige betaling",
        "30 dagen toegang tot je rapport",
      ],
      cta: "Start mijn analyse",
    },
    bundle: {
      badge: "Populair · bespaar 33%",
      label: "3-pack bundle",
      price: "€9,99",
      sub: "3 analyses · gem. € 3,33 per analyse",
      features: [
        "3 analyses voor 1 e-mailadres",
        "Alles uit het standaardpakket",
        "Credits 12 maanden geldig",
        "Ideaal om offertes te vergelijken",
      ],
      cta: "Koop 3-pack",
      secondary: "Al een 3-pack? Wissel je credit in →",
    },
    securityLabel: "Beveiligd betalen via MultiSafepay · 256-bit encryptie",
    methods: ["Bancontact", "iDEAL", "Visa", "Mastercard", "Apple Pay", "Google Pay"],
  },
  faq: {
    eyebrow: "Veelgesteld",
    heading: "Vragen die we vaak krijgen",
    items: [
      {
        q: "Hoe nauwkeurig is de analyse?",
        a: "Onze AI is getraind op duizenden Belgische en Nederlandse markttarieven en wordt continu bijgewerkt. De analyse geeft een betrouwbare indicatie, maar blijft een advies — bij twijfel raden we aan een tweede offerte op te vragen.",
      },
      {
        q: "Welke soorten offertes kan ik uploaden?",
        a: "Renovatie, elektriciteit, schilderwerk, tuinaanleg, IT, verhuizing, onderhoud en meer. Heb je een ander type? Kies dan 'Andere' in de wizard en we passen de analyse aan.",
      },
      {
        q: "Wat gebeurt er met mijn gegevens?",
        a: "Documenten en analyseresultaten worden 30 dagen bewaard en daarna automatisch en permanent verwijderd. We delen nooit gegevens met derden behalve onze technische dienstverleners (analyse en betaalproviders).",
      },
    ],
    linkLabel: "Bekijk alle veelgestelde vragen",
  },
};

function isPlainObject(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

// Deep-merge `override` over `defaults`, driven entirely by the SHAPE of
// `defaults`. Objects recurse by default's keys; arrays merge by index up to
// the default's length; primitives take the override unless it's
// null/undefined or an empty/whitespace-only string. This guarantees:
//   - unknown keys in override are ignored (can't break the layout)
//   - a blank field falls back to the original copy
//   - fixed structure (default array length wins)
export function mergeContent(defaults, override) {
  if (Array.isArray(defaults)) {
    if (!Array.isArray(override)) return defaults;
    return defaults.map((d, i) => mergeContent(d, override[i]));
  }
  if (isPlainObject(defaults)) {
    if (!isPlainObject(override)) return defaults;
    const out = {};
    for (const key of Object.keys(defaults)) {
      out[key] = mergeContent(defaults[key], override[key]);
    }
    return out;
  }
  // primitive
  if (override === undefined || override === null) return defaults;
  if (typeof override === "string" && override.trim() === "") return defaults;
  return override;
}

// Merged, ready-to-render homepage content. Components read their slice, e.g.
//   const { hero } = useHomepageContent();
export function useHomepageContent() {
  const cfg = usePublicConfig();
  return useMemo(() => mergeContent(HOMEPAGE_DEFAULTS, cfg?.homepage), [cfg]);
}
